/* The prayer-time chime: a short, soft three-note bell, made in the app (no
   download, so it works offline). It plays when the adhan can't:
   - you chose it as the sound (Settings → Notifications → Adhan sound);
   - the adhan recording can't load (offline, or the server is down);
   - the Qurʾān or ruqyah is playing. The adhan never talks over them, but the
     chime is mixed in softly through Web Audio, so the recitation keeps going.
   Like the adhan it needs one tap in the app since it was opened (iOS rule),
   and with the app closed only the notification sound can play. */

const RATE = 22050;
/* A bell: each note is a few partials with an exponential decay. Descending
   E5 → C5 → G4, the last held longest, so it reads as "time", not "alert". */
const NOTES: [freq: number, start: number, length: number][] = [[659.25, 0, 1.6], [523.25, 0.55, 1.8], [392.0, 1.1, 2.6]];
const PARTIALS: [mult: number, gain: number, decay: number][] = [[1, 1, 1.4], [2, 0.35, 2.6], [3, 0.12, 4], [4.2, 0.06, 6]];
const SECONDS = 3.9;

let wav: ArrayBuffer | null = null;
/** The chime as a 16-bit mono WAV. */
export function chimeWav(): ArrayBuffer {
  if (wav) return wav;
  const n = Math.round(RATE * SECONDS);
  const pcm = new Float32Array(n);
  for (const [f, start, len] of NOTES) {
    const s0 = Math.round(start * RATE), sl = Math.round(len * RATE);
    for (let i = 0; i < sl && s0 + i < n; i++) {
      const t = i / RATE;
      const attack = Math.min(1, t / 0.008);                   // no click at the start
      let v = 0;
      for (const [m, g, d] of PARTIALS) v += g * Math.exp(-d * t) * Math.sin(2 * Math.PI * f * m * t);
      pcm[s0 + i] += v * attack * 0.32;
    }
  }
  const fade = Math.round(0.25 * RATE);                         // and none at the end
  for (let i = 0; i < fade; i++) pcm[n - 1 - i] *= i / fade;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const w = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, RATE, true); v.setUint32(28, RATE * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, 'data'); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) v.setInt16(44 + i * 2, Math.max(-1, Math.min(1, pcm[i])) * 0x7fff, true);
  return (wav = buf);
}

let url: string | null = null;
/** For an <audio> element (the adhan's own, already unlocked, so it plays on the lock screen too). */
export function chimeUrl(): string {
  return (url ??= URL.createObjectURL(new Blob([chimeWav()], { type: 'audio/wav' })));
}

/* Web Audio, for playing over the Qurʾān: an <audio> element would take over
   iOS's audio session and pause the reciter; a Web Audio source mixes with it. */
let ctx: AudioContext | null = null;
let decoded: Promise<AudioBuffer> | null = null;
const context = () => {
  if (!ctx) { const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext; if (AC) ctx = new AC(); }
  return ctx;
};

/** Call from a user gesture (with the adhan's unlock): lets the mixed chime play later. */
export function unlockChime() {
  const c = context();
  if (c && c.state !== 'running') void c.resume().catch(() => {});
}

/** Play the chime mixed over whatever is playing. Resolves false if Web Audio is locked or missing. */
export async function playChimeOver(volume = 0.6): Promise<boolean> {
  const c = context();
  if (!c) return false;
  try {
    if (c.state !== 'running') await c.resume();
    if (c.state !== 'running') return false;
    decoded ??= c.decodeAudioData(chimeWav().slice(0));
    const src = c.createBufferSource();
    src.buffer = await decoded;
    const g = c.createGain();
    g.gain.value = volume;
    src.connect(g).connect(c.destination);
    src.start();
    return true;
  } catch {
    return false;
  }
}
